import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { useAuth } from './AuthContext';
import paymentService, { PaymentMethod } from '../services/paymentService';
import { validateEnv } from '../config/env';

interface PaymentState {
  paymentMethods: PaymentMethod[];
  loading: boolean;
  error: string | null;
  stripeInitialized: boolean;
}

type PaymentAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_PAYMENT_METHODS'; payload: PaymentMethod[] }
  | { type: 'ADD_PAYMENT_METHOD'; payload: PaymentMethod }
  | { type: 'REMOVE_PAYMENT_METHOD'; payload: string }
  | { type: 'UPDATE_PAYMENT_METHOD'; payload: PaymentMethod }
  | { type: 'SET_STRIPE_INITIALIZED'; payload: boolean }
  | { type: 'RESET_STATE' };

const initialState: PaymentState = {
  paymentMethods: [],
  loading: false,
  error: null,
  stripeInitialized: false,
};

function paymentReducer(state: PaymentState, action: PaymentAction): PaymentState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_PAYMENT_METHODS':
      return { ...state, paymentMethods: action.payload };
    case 'ADD_PAYMENT_METHOD':
      return { 
        ...state, 
        paymentMethods: [...state.paymentMethods, action.payload] 
      };
    case 'REMOVE_PAYMENT_METHOD':
      return { 
        ...state, 
        paymentMethods: state.paymentMethods.filter(pm => pm.id !== action.payload) 
      };
    case 'UPDATE_PAYMENT_METHOD':
      return { 
        ...state, 
        paymentMethods: state.paymentMethods.map(pm => 
          pm.id === action.payload.id ? action.payload : pm
        ) 
      };
    case 'SET_STRIPE_INITIALIZED':
      return { ...state, stripeInitialized: action.payload };
    case 'RESET_STATE':
      return initialState;
    default:
      return state;
  }
}

interface PaymentContextType {
  state: PaymentState;
  initializeStripe: () => Promise<void>;
  loadPaymentMethods: () => Promise<void>;
  addPaymentMethod: (paymentMethodData: any, setAsDefault?: boolean) => Promise<boolean>;
  deletePaymentMethod: (paymentMethodId: string) => Promise<boolean>;
  setDefaultPaymentMethod: (paymentMethodId: string) => Promise<boolean>;
  getDefaultPaymentMethod: () => PaymentMethod | null;
  clearError: () => void;
}

const PaymentContext = createContext<PaymentContextType | undefined>(undefined);

export const PaymentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(paymentReducer, initialState);
  const { auth } = useAuth();

  // Initialize Stripe when component mounts
  useEffect(() => {
    initializeStripe();
  }, []);

  // Load payment methods when user authentication changes
  useEffect(() => {
    if (auth.isAuthenticated) {
      loadPaymentMethods();
    } else {
      dispatch({ type: 'RESET_STATE' });
    }
  }, [auth.isAuthenticated]);

  const initializeStripe = async () => {
    try {
      // Validate environment variables first
      if (!validateEnv()) {
        throw new Error('Missing required environment variables for payment system');
      }
      
      await paymentService.initializeStripe();
      dispatch({ type: 'SET_STRIPE_INITIALIZED', payload: true });
    } catch (error: any) {
      console.error('Failed to initialize Stripe:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to initialize payment system' });
    }
  };

  const loadPaymentMethods = async () => {
    if (!auth.isAuthenticated) return;

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });

      const response = await paymentService.getSavedPaymentMethods();

      if (response.success) {
        dispatch({ type: 'SET_PAYMENT_METHODS', payload: response.data || [] });
      } else {
        dispatch({ type: 'SET_ERROR', payload: response.error?.message || 'Failed to load payment methods' });
      }
    } catch (error: any) {
      console.error('Error loading payment methods:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message || 'Failed to load payment methods' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const addPaymentMethod = async (
    paymentMethodData: any,
    setAsDefault: boolean = false
  ): Promise<boolean> => {
    try {
      dispatch({ type: 'SET_ERROR', payload: null });

      // Create setup intent
      const setupIntentResponse = await paymentService.createSetupIntent();

      if (!setupIntentResponse.success || !setupIntentResponse.data) {
        throw new Error(setupIntentResponse.error?.message || 'Failed to create setup intent');
      }

      const { clientSecret } = setupIntentResponse.data;

      // Create payment method
      const paymentMethod = await paymentService.createPaymentMethod(
        paymentMethodData.cardElement,
        paymentMethodData.billingDetails
      );

      // Confirm setup intent
      const setupIntent = await paymentService.confirmSetupIntent(
        clientSecret,
        paymentMethod.id
      );

      if (setupIntent.status === 'succeeded') {
        // Save payment method to backend
        const saveResponse = await paymentService.savePaymentMethod(
          paymentMethod,
          setAsDefault
        );

        if (saveResponse.success) {
          // Reload payment methods to get updated list
          await loadPaymentMethods();
          return true;
        } else {
          throw new Error(saveResponse.error?.message || 'Failed to save payment method');
        }
      } else {
        throw new Error('Failed to confirm payment method setup');
      }
    } catch (error: any) {
      console.error('Error adding payment method:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message || 'Failed to add payment method' });
      return false;
    }
  };

  const deletePaymentMethod = async (paymentMethodId: string): Promise<boolean> => {
    try {
      dispatch({ type: 'SET_ERROR', payload: null });

      const response = await paymentService.deletePaymentMethod(paymentMethodId);

      if (response.success) {
        dispatch({ type: 'REMOVE_PAYMENT_METHOD', payload: paymentMethodId });
        return true;
      } else {
        throw new Error(response.error?.message || 'Failed to delete payment method');
      }
    } catch (error: any) {
      console.error('Error deleting payment method:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message || 'Failed to delete payment method' });
      return false;
    }
  };

  const setDefaultPaymentMethod = async (paymentMethodId: string): Promise<boolean> => {
    try {
      dispatch({ type: 'SET_ERROR', payload: null });

      const response = await paymentService.setDefaultPaymentMethod(paymentMethodId);

      if (response.success) {
        // Update local state
        const updatedMethods = state.paymentMethods.map(pm => ({
          ...pm,
          isDefault: pm.id === paymentMethodId
        }));
        dispatch({ type: 'SET_PAYMENT_METHODS', payload: updatedMethods });
        return true;
      } else {
        throw new Error(response.error?.message || 'Failed to set default payment method');
      }
    } catch (error: any) {
      console.error('Error setting default payment method:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message || 'Failed to set default payment method' });
      return false;
    }
  };

  const getDefaultPaymentMethod = (): PaymentMethod | null => {
    return state.paymentMethods.find(pm => pm.isDefault) || null;
  };

  const clearError = () => {
    dispatch({ type: 'SET_ERROR', payload: null });
  };

  const value: PaymentContextType = {
    state,
    initializeStripe,
    loadPaymentMethods,
    addPaymentMethod,
    deletePaymentMethod,
    setDefaultPaymentMethod,
    getDefaultPaymentMethod,
    clearError,
  };

  return (
    <PaymentContext.Provider value={value}>
      {children}
    </PaymentContext.Provider>
  );
};

export const usePayment = (): PaymentContextType => {
  const context = useContext(PaymentContext);
  if (context === undefined) {
    throw new Error('usePayment must be used within a PaymentProvider');
  }
  return context;
};